// What the phone layout asks of the page around it, mounted only while it shows: body.is-phone for its styles; the
// visible height above the keyboard (--vv-h, --vv-top) so the chat's box is never under it; body.is-typing while a
// field has the keyboard, so the bar steps aside; no zoom on an iPhone when a field takes focus (it still pinches);
// and the phone's Back closing what is open over the board, top first, instead of leaving the page.

import { useEffect, useRef } from "react";
import { closeChat, useChat } from "../agent/store";
import { closeContextMenu, useMenu } from "../chrome/ContextMenu";
import { currentBoard, get, set, useStore } from "../state/store";
import { kindOf } from "../../shared/boardKinds";
import { openDeal, usePipeUi } from "../boards/pipeline/state";
import { openPost, useCalUi } from "../boards/calendar/state";
import { closeFull, closeSheet, usePhone } from "./state";
import { ownsTouch } from "./gestures";

export function PhoneChrome() {
  useEffect(() => {
    document.body.classList.add("is-phone");
    return () => document.body.classList.remove("is-phone", "is-typing");
  }, []);
  useViewport();
  useNoZoomOnFocus();
  useTyping();
  useBack();
  return null;
}

function useViewport() {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    const put = () => {
      root.style.setProperty("--vv-h", `${Math.round(vv ? vv.height : window.innerHeight)}px`);
      root.style.setProperty("--vv-top", `${Math.round(vv ? vv.offsetTop : 0)}px`);
    };
    put();
    vv?.addEventListener("resize", put);
    vv?.addEventListener("scroll", put);
    window.addEventListener("resize", put);
    return () => {
      vv?.removeEventListener("resize", put);
      vv?.removeEventListener("scroll", put);
      window.removeEventListener("resize", put);
      root.style.removeProperty("--vv-h");
      root.style.removeProperty("--vv-top");
    };
  }, []);
}

/** iOS zooms the page into a field under 16px when it takes focus, and leaves it zoomed; maximum-scale stops that, and iOS still lets a person pinch. */
function useNoZoomOnFocus() {
  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const meta = document.querySelector('meta[name="viewport"]');
    const was = meta?.getAttribute("content");
    if (!ios || !meta || !was || was.includes("maximum-scale")) return;
    meta.setAttribute("content", `${was}, maximum-scale=1`);
    return () => meta.setAttribute("content", was);
  }, []);
}

function useTyping() {
  useEffect(() => {
    // a field that goes away while it has focus (the chat closing) sends no focusout, so the check also runs on a beat
    let beat: ReturnType<typeof setInterval> | null = null;
    const check = () => {
      const typing = ownsTouch(document.activeElement) && document.activeElement?.tagName !== "IFRAME";
      document.body.classList.toggle("is-typing", typing);
      if (typing && !beat) beat = setInterval(check, 400);
      if (!typing && beat) {
        clearInterval(beat);
        beat = null;
      }
    };
    const later = () => setTimeout(check, 60);
    document.addEventListener("focusin", check);
    document.addEventListener("focusout", later);
    return () => {
      if (beat) clearInterval(beat);
      document.removeEventListener("focusin", check);
      document.removeEventListener("focusout", later);
    };
  }, []);
}

/** What is open over the board, top first, each with how it closes. */
function useLayers(): (() => void)[] {
  const menu = useMenu((s) => s.open);
  const chatOpen = useChat((s) => s.open);
  const full = usePhone((s) => !!s.full);
  const sheet = usePhone((s) => !!s.sheet);
  const panel = useStore((s) => !!s.panel);
  const boardId = useStore((s) => s.boardId);
  const kind = useStore((s) => kindOf(currentBoard(s)));
  // a deal or a post is open only on its own board: one left open on another board is not over this one (a deal's
  // open id is one for every pipeline, so it counts only when the deal is on the board shown)
  const post = useCalUi((s) => kind === "calendar" && !!boardId && !!s.open[boardId]);
  const dealId = usePipeUi((s) => s.open);
  const deal = useStore((s) => kind === "pipeline" && !!dealId && s.entries[dealId]?.boardId === s.boardId);
  const layers: (() => void)[] = [];
  if (menu) layers.push(closeContextMenu);
  if (chatOpen) layers.push(closeChat);
  if (full) layers.push(closeFull);
  if (sheet) layers.push(closeSheet);
  if (panel) layers.push(() => set({ panel: null, panelArg: null }));
  if (deal) layers.push(() => openDeal(null));
  if (post && boardId) layers.push(() => openPost(boardId, null));
  return layers;
}

/**
 * Each layer opened over the board puts a step in the browser's history, so Back closes it rather than leaving the
 * board. A layer closed on screen takes its step back out. A close that is refused (words not saved yet) puts the
 * step back.
 */
function useBack() {
  const layers = useLayers();
  const now = useRef(layers);
  now.current = layers;
  const pushed = useRef(0);
  const ours = useRef(0);
  const depth = layers.length;
  useEffect(() => {
    if (depth > pushed.current) {
      for (; pushed.current < depth; pushed.current++) history.pushState({ cbPhone: pushed.current + 1 }, "");
    } else if (depth < pushed.current) {
      ours.current++;
      history.go(depth - pushed.current);
      pushed.current = depth;
    }
  }, [depth]);
  useEffect(() => {
    const onPop = () => {
      if (ours.current > 0) {
        ours.current--;
        return;
      }
      if (pushed.current === 0) return;
      pushed.current--;
      now.current[0]?.();
      // still open: it kept its words, so it keeps its step
      setTimeout(() => {
        if (now.current.length > pushed.current) {
          history.pushState({ cbPhone: pushed.current + 1 }, "");
          pushed.current++;
        }
      }, 0);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // a layout that leaves the phone's takes its steps with it
  useEffect(
    () => () => {
      if (pushed.current > 0 && get().mobile === false) history.go(-pushed.current);
    },
    [],
  );
}
